/**
 * Shared visuals for selectable "card" controls.
 *
 * One implementation backs both selection primitives so the two can never
 * drift apart:
 *   - `RadioCard`    (lib/base/radio-buttons) — single-select
 *   - `CheckboxCard` (lib/base/forms/checkbox) — multi-select
 *
 * Two variants:
 *   - `'card'`   — bordered card. The default, and byte-for-byte the look
 *     `RadioCard` shipped with, so existing consumers are unaffected.
 *   - `'button'` — pixel-identical to a secondary Button, composed from the
 *     Button component's exported style tokens rather than copied classes, so
 *     option cards stay in lockstep with secondary Buttons.
 *
 * Focus lives on the visually-hidden native input that react-aria renders
 * inside each card, so the focus ring is driven by the `isFocusVisible` render
 * prop rather than CSS `:focus-visible` on the card element.
 */
import { styles as buttonStyles } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

export type SelectionCardVariant = 'card' | 'button';

/** Card size. Only meaningful for the `'button'` variant. */
export type SelectionCardSize = 'md' | 'xl';

export interface SelectionCardRenderState {
  isSelected: boolean;
  isFocusVisible: boolean;
  isDisabled: boolean;
}

/**
 * Visual classes for a selection card. Layout that depends on the control
 * (e.g. `RadioCard`'s indicator row) is applied by the component, not here.
 */
export function selectionCardClassName(
  variant: SelectionCardVariant,
  size: SelectionCardSize,
  state: SelectionCardRenderState
): string {
  return variant === 'button'
    ? buttonVariantClassName(size, state)
    : cardVariantClassName(state);
}

function cardVariantClassName(state: SelectionCardRenderState): string {
  return cx(
    'flex cursor-pointer gap-3 rounded-lg border p-4 transition-colors',
    state.isSelected
      ? 'border-[var(--color-brand-600)] bg-[var(--color-bg-secondary)]'
      : 'border-[var(--color-border-secondary)] hover:bg-[var(--color-bg-secondary)]',
    state.isDisabled && 'cursor-not-allowed opacity-50',
    state.isFocusVisible && 'outline-2 outline-offset-2 outline-focus-ring'
  );
}

function buttonVariantClassName(
  size: SelectionCardSize,
  state: SelectionCardRenderState
): string {
  return cx(
    buttonStyles.common.root,
    buttonStyles.sizes[size].root,
    buttonStyles.colors.secondary.root,
    'w-full min-w-0',
    state.isSelected &&
      'bg-brand-50 ring-2 ring-brand-600 hover:bg-brand-50 hover:ring-brand-600',
    state.isFocusVisible && 'outline-2 outline-offset-2',
    // Applied last so it overrides the selected-state background/ring above
    // (react-aria surfaces disabled via `data-disabled` on the label, not the
    // native `disabled` attribute, so it must be styled explicitly here).
    // The `hover:` counterparts are required: the secondary Button tokens carry
    // `hover:text-secondary_hover` / `hover:*:data-icon:text-fg-quaternary_hover`,
    // which would otherwise re-apply an enabled-looking color when the pointer
    // rests on a disabled card. Mirror the Button's disabled treatment so the
    // disabled styles always win on hover (text and icon).
    state.isDisabled &&
      'cursor-not-allowed bg-disabled_subtle text-disabled ring-1 ring-disabled hover:bg-disabled_subtle hover:text-disabled hover:ring-disabled *:data-icon:text-fg-disabled_subtle hover:*:data-icon:text-fg-disabled_subtle'
  );
}
